import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {
  BannerLogServiceDetailReq,
  BannerLogServiceDetailSearchResDTO,
  BannerServiceDetailSaveReqDTO,
  BannerServiceDetailSaveResDTO,
  BannerServiceDetailSearchReqDTO,
  BannerServiceDetailSearchResDTO,
  BannerServiceDetailUpdateReqDTO,
  BannerServiceDetailUpdateResDTO,
  UpdateBannerPosition,
  UploadImageResDTO
} from 'src/models/Banner';
import { tourismClassifiedAPIRoutes } from '../tourism-classified';

export const BannerServiceDetail = createApi({
  reducerPath: 'BannerServiceDetail',
  tagTypes: ['BannerServiceTag'],
  baseQuery: fetchBaseQuery({
    baseUrl: tourismClassifiedAPIRoutes.baseUrl,
    prepareHeaders: (headers) => {
      // If we have a token set in local storage, let's assume that we should be passing it.
      const token = localStorage.getItem('jwtToken');
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    }
  }),
  endpoints: (builder) => ({
    saveBanner: builder.mutation<
      BannerServiceDetailSaveResDTO,
      BannerServiceDetailSaveReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.saveBanner}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['BannerServiceTag']
    }),

    updateBanner: builder.mutation<
      BannerServiceDetailUpdateResDTO,
      BannerServiceDetailUpdateReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.updateBanner}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['BannerServiceTag']
    }),

    findBannerBySearch: builder.query<
      BannerServiceDetailSearchResDTO,
      BannerServiceDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.findBannerBySearch}`,
        method: 'POST',
        body
      }),
      providesTags: ['BannerServiceTag']
    }),
    findBannerBySearchMutation: builder.mutation<
      BannerServiceDetailSearchResDTO,
      BannerServiceDetailSearchReqDTO
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.findBannerBySearch}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['BannerServiceTag']
    }),

    findBannerById: builder.mutation({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.findBannerById}/${id}`,
        method: 'GET'
      })
    }),
    bannerPosition: builder.mutation<
      UpdateBannerPosition,
      UpdateBannerPosition
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.updateBannerSequence}`,
        method: 'PUT',
        body
      }),
      invalidatesTags: ['BannerServiceTag']
    }),

    deleteBanner: builder.mutation<boolean, number>({
      query: (id) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.deleteBanner}/${id}`,
        method: 'DELETE'
      }),
      invalidatesTags: ['BannerServiceTag']
    }),
    uploadImage: builder.mutation<
      UploadImageResDTO,
      {
        body: FormData;
      }
    >({
      query: ({ body }) => ({
        url: `${tourismClassifiedAPIRoutes.BannerController.endpoint}/${tourismClassifiedAPIRoutes.BannerController.uploadImage}`,
        method: 'POST',
        body
      }),
      invalidatesTags: ['BannerServiceTag']
    }),

    getBannerLogData: builder.query<
      BannerLogServiceDetailSearchResDTO,
      BannerLogServiceDetailReq
    >({
      query: (body) => ({
        url: `${tourismClassifiedAPIRoutes.BannerLogController.endpoint}/${tourismClassifiedAPIRoutes.BannerLogController.getBannerLogData}`,
        method: 'POST',
        body
      }),
      providesTags: ['BannerServiceTag']
    })
  })
});
export const {
  useDeleteBannerMutation,
  useFindBannerByIdMutation,
  useFindBannerBySearchQuery,
  useSaveBannerMutation,
  useUpdateBannerMutation,
  useUploadImageMutation,
  useBannerPositionMutation,
  useFindBannerBySearchMutationMutation,
  useGetBannerLogDataQuery
} = BannerServiceDetail;
